import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PiUploadSimple,
  PiClipboardText,
  PiMapPin,
  PiRoadHorizon,
} from "react-icons/pi";
import { FaVideo } from "react-icons/fa";
import { HiDocumentReport } from "react-icons/hi";
import { MdLogout, MdDashboard, MdSettings, MdOutlineConfirmationNumber, MdCompareArrows } from "react-icons/md";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useBranding } from "../../contexts/BrandingContext";
import useAuth from "../../hooks/useAuth";
import { isKotaOrg } from "../../utils/kotaDefectConfig";
import RVlogo from "../../assets/rv_logo.png";
import { BRAND } from "../../brand";
import SuperAdminOrgPicker from "./SuperAdminOrgPicker";

const TICKETS_MODULE = {
  key: "tickets", label: "Tickets",
  icon: MdOutlineConfirmationNumber, route: "/pavement/tickets",
};

const adminModules = [
  { key: "dashboard", label: "Dashboard", icon: MdDashboard, route: "/home" },
  { key: "gis", label: "GIS", icon: PiMapPin, route: null },
  { key: "report", label: "Report", icon: HiDocumentReport, route: "/pavement/app" },
  { key: "weeklyComparison", label: "Weekly Comparison", icon: MdCompareArrows, route: "/pavement/weeklyComparison" },
  // { key: "prepost", label: "Pre/Post Analysis", icon: MdCompareArrows, route: "/pavement/prePostAnalysis" },
  // { key: "road", label: "Road", icon: PiUploadSimple, route: "/road/dashcam" },
  { key: "roadSurveys", label: "Road Surveys", icon: PiRoadHorizon, route: "/road/surveys" },
  // { key: "survey", label: "Surveys", icon: PiClipboardText, route: "/pavement/viewTask" },
  { key: "video", label: "Video Library", icon: FaVideo, route: "/video/app" },
  { key: "tickets", label: "Tickets", icon: MdOutlineConfirmationNumber, route: "/pavement/tickets" },
  { key: "settings", label: "Settings", icon: MdSettings, route: "/pavement/settings" },
];

const surveyorModules = [
  // { key: "survey", label: "Survey", icon: PiClipboardText, route: "/pavement/viewTask" },
  // { key: "road", label: "Road", icon: PiUploadSimple, route: "/road/dashcam" },
  { key: "roadSurveys", label: "Road Surveys", icon: PiRoadHorizon, route: "/road/surveys" },
  { key: "videoLibrary", label: "Video Library", icon: FaVideo, route: "/video/app" },
  // { key: "tickets", label: "Tickets", icon: MdOutlineConfirmationNumber, route: "/pavement/tickets" },
];

const authorityModules = [
  { key: "dashboard", label: "Dashboard", icon: MdDashboard, route: "/home" },
  { key: "tickets",   label: "Tickets",   icon: MdOutlineConfirmationNumber, route: "/pavement/tickets" },
];

const AppSidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { brandingSettings } = useBranding();
  const { role, user, logout } = useAuth();
  const isSuperAdmin = role === "SuperAdmin";
  const isSurveyor  = role === "Surveyor";
  const isAuthority = role === "Authority";
  const isKhammamUser = (user?.email || "").toLowerCase() === "khammam@roadvision.ai";
  // Orgs that should not see Pre/Post Analysis in the sidebar. Matched
  // against the user's organization (case-insensitive, whitespace-stripped)
  // and also localStorage["organization"] so SuperAdmin's active-org switch
  // is respected. Keep this list in lowercase.
  const ORGS_HIDE_PREPOST = new Set([
    "bangaloreorg",   // IBI Dashboard (Bengaluru)
    "kotaorg",
    "varanasiorg",
  ]);
  const _orgLower = (s) => (s || "").toString().trim().toLowerCase();
  const _activeOrg = (() => {
    try { return _orgLower(window.localStorage.getItem("organization")); }
    catch { return ""; }
  })();
  const hidePrepost =
    isKhammamUser ||
    ORGS_HIDE_PREPOST.has(_orgLower(user?.organization)) ||
    ORGS_HIDE_PREPOST.has(_activeOrg);
  // Kota-only addition: insert "Tickets" between Video Library and Settings
  // so the Kota dashboard gets a ticket-tracking entry without affecting any
  // other org's sidebar.
  const withKotaTickets = (mods) => {
    if (!isKotaOrg()) return mods;
    if (mods.some(m => m.key === "tickets")) return mods;
    const settingsIdx = mods.findIndex(m => m.key === "settings");
    const insertAt = settingsIdx === -1 ? mods.length : settingsIdx;
    return [...mods.slice(0, insertAt), TICKETS_MODULE, ...mods.slice(insertAt)];
  };
  // Weekly Comparison is limited to specific pilot accounts.
  const WEEKLY_COMPARISON_USERS = new Set([
    "upwest@roadvision.ai",
    "superadmin@ador.ai",
    "upwest@ador.ai",
    "superadmin@roadvision.ai",
    "assam@ador.ai",
    "weekly@roadvision.ai",
  ]);
  const canSeeWeeklyComparison = WEEKLY_COMPARISON_USERS.has((user?.email || "").toLowerCase());
  const filteredAdminModules = (hidePrepost
    ? adminModules.filter(m => m.key !== "prepost")
    : adminModules
  ).filter(m => m.key !== "weeklyComparison" || canSeeWeeklyComparison);
  const baseModules = isAuthority
    ? authorityModules
    : isSurveyor
      ? surveyorModules
      : filteredAdminModules;
  const modules = isAuthority ? baseModules : withKotaTickets(baseModules);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getActiveKey = () => {
    const path = location.pathname;
    if (path === "/home") return "dashboard";
    if (path.includes("/multiMap")) return "gis";
    if (path.includes("/viewTask") || path.includes("/myTask")) return "survey";
    if (path.includes("/video")) return "video";
    if (path.includes("/prePostAnalysis")) return "prepost";
    if (path.includes("/weeklyComparison")) return "weeklyComparison";
    if (path.includes("/pavement/app") || path.includes("/report") || path.includes("/multiReport")) return "report";
    if (path.includes("/settings") || path.includes("/manageUser") || path.includes("/profileBranding")) return "settings";
    if (path.includes("/pavement/tickets")) return "tickets";
    if (path.includes("/road/surveys")) return "roadSurveys";
    if (path.includes("/road") || path.includes("/dashcam")) return "road";
    return "dashboard";
  };

  const activeKey = getActiveKey();

  const handleModuleClick = (mod) => {
    if (mod.key === "gis") {
      // Navigate instantly — MultiMap will fetch its own data
      navigate("/pavement/multiMap");
      return;
    }
    if (mod.route) {
      navigate(mod.route);
    }
  };

  return (
    <aside
      className={`h-screen flex flex-col border-r border-[#1a3a5c] transition-all duration-300 ease-in-out flex-shrink-0 ${
        collapsed ? "w-[68px]" : "w-[230px]"
      }`}
      style={{ position: "relative", backgroundColor: "hsl(217, 64%, 18%)", fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Logo */}
      <div className={`flex items-center justify-center border-b border-white/10 ${collapsed ? "px-2 py-3" : "px-5 py-4"}`}>
        <img
          src={collapsed ? (BRAND.logoCollapsed || "/rvnewlog-removebg.png") : (BRAND.logoSidebar || BRAND.logo || RVlogo)}
          alt="Logo"
          className={`object-contain ${collapsed ? "w-10 h-10" : "h-11 w-auto max-w-[195px]"}`}
        />
      </div>

      {/* SuperAdmin org picker — hidden for now so SuperAdmin always sees the
          aggregate all-orgs (NHAI) view and cannot select a single org.
      {isSuperAdmin && <SuperAdminOrgPicker collapsed={collapsed} />}
      */}

      {/* Category Label */}
      {!collapsed && !isSurveyor && !isAuthority && !isSuperAdmin && (
        <div className="px-5 pt-3 pb-1">
          <span className="text-[11px] font-semibold tracking-widest text-white/50 uppercase">
            Road Intelligence
          </span>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-3">
        {modules.map((mod, idx) => {
          const isActive = activeKey === mod.key;
          const Icon = mod.icon;

          // Add category separator before management items
          const showManagementLabel = !collapsed && !isSurveyor && !isAuthority && mod.key === "roadSurveys";

          return (
            <React.Fragment key={mod.key}>
              {showManagementLabel && (
                <div className="px-2 pt-3 pb-1">
                  <span className="text-[11px] font-semibold tracking-widest text-white/50 uppercase">
                    Management
                  </span>
                </div>
              )}
              <button
                onClick={() => handleModuleClick(mod)}
                className={`w-full flex items-center gap-3 transition-all duration-200 rounded-lg mb-0.5 ${
                  collapsed ? "px-0 py-2.5 justify-center" : "px-4 py-2.5"
                } ${
                  isActive
                    ? "bg-white/15 text-white font-semibold"
                    : "text-white/80 hover:bg-white/10 hover:text-white"
                }`}
                title={collapsed ? mod.label : ""}
              >
                <div
                  className={`flex items-center justify-center flex-shrink-0 ${
                    collapsed ? "w-5 h-5" : "w-5 h-5"
                  }`}
                >
                  <Icon size={collapsed ? 19 : 17} />
                </div>
                {!collapsed && (
                  <span className="text-[14px] font-medium whitespace-nowrap">
                    {mod.label}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="flex flex-col">
        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`w-full py-2 flex items-center gap-3 border-t border-white/10 text-white/70 hover:text-white transition-all ${
            collapsed ? "justify-center px-3" : "px-4"
          }`}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <IoChevronForward size={16} /> : <IoChevronBack size={16} />}
          {!collapsed && <span className="text-[13px]">Collapse</span>}
        </button>

        {/* User Info */}
        <div className={`flex items-center gap-2.5 py-2 border-t border-white/10 ${collapsed ? "justify-center px-3" : "px-4"}`}>
          <div className="w-8 h-8 rounded-full bg-[#1a2d54] flex items-center justify-center flex-shrink-0 border-2 border-blue-400/50">
            <span className="text-[11px] font-bold text-white">
              {(user?.name || "U")[0].toUpperCase()}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-semibold text-white truncate">
                  {user?.name || "User"}
                </span>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-500/30 text-blue-200 tracking-wider flex-shrink-0">
                  {role || "User"}
                </span>
              </div>
              <div className="text-[10px] text-white/50 truncate">
                {user?.email || ""}
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`w-full py-2 flex items-center gap-3 text-white/70 hover:text-red-400 transition-all ${
            collapsed ? "justify-center px-3" : "px-4"
          }`}
          title="Logout"
        >
          <MdLogout size={16} />
          {!collapsed && <span className="text-[13px]">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default AppSidebar;
